/**
 * Cookie / tracking consent core (framework-agnostic).
 *
 * Single source of truth for GDPR + EU ePrivacy (PT Lei 41/2004) consent. The
 * inline default-deny script in Views/Main/Index.cshtml reads the SAME
 * localStorage key and shape BEFORE Google Tag Manager loads, so analytics /
 * marketing tags stay denied (Google Consent Mode v2) until the user opts in.
 *
 * Keep this module dependency-free (no React, no MUI) so both the React app and
 * the raw inline bootstrap script can agree on the storage contract.
 */

/** Storage key — MUST match the inline gate in Views/Main/Index.cshtml. */
export const CONSENT_STORAGE_KEY = "converge_cookie_consent";

/** Mirror cookie so the server can read consent on a request if ever needed. */
export const CONSENT_COOKIE_NAME = "converge_cookie_consent";

/**
 * Bump when the cookie/privacy policy materially changes. A stored decision
 * with an older version is treated as "no decision" → the banner re-prompts.
 */
export const CURRENT_POLICY_VERSION = 1;

/** Non-essential categories the user can toggle. Necessary is always on. */
export interface ConsentCategories {
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
}

export interface ConsentRecord {
    version: number;
    /** ISO-8601 timestamp of the decision. */
    timestamp: string;
    categories: ConsentCategories;
    /** Locale active when the choice was made (best-effort, for the audit log). */
    locale?: string;
}

export const DENY_ALL: ConsentCategories = {
    analytics: false,
    marketing: false,
    preferences: false
};

export const GRANT_ALL: ConsentCategories = {
    analytics: true,
    marketing: true,
    preferences: true
};

type ConsentListener = (categories: ConsentCategories) => void;
const listeners = new Set<ConsentListener>();

const isBrowser = (): boolean => typeof window !== "undefined";

/**
 * The site (www), the Sports host and the playground are all *.fidava-pt.com:
 * the mirror cookie is written for the parent domain so one decision covers
 * them all (localStorage is per host, so on its own the playground never knew
 * the visitor had accepted analytics on the site, and sent nothing).
 */
const SHARED_COOKIE_DOMAIN = "fidava-pt.com";

const cookieDomainAttribute = (): string => {
    const host = window.location.hostname.toLowerCase();
    return host === SHARED_COOKIE_DOMAIN || host.endsWith(`.${SHARED_COOKIE_DOMAIN}`) ? `; domain=.${SHARED_COOKIE_DOMAIN}` : "";
};

let sharedCookieSynced = false;

const readMirrorCookie = (): string | null => {
    try {
        const prefix = `${CONSENT_COOKIE_NAME}=`;
        const hit = document.cookie.split(";").map((c) => c.trim()).find((c) => c.startsWith(prefix));
        return hit ? decodeURIComponent(hit.slice(prefix.length)) : null;
    } catch {
        return null;
    }
};

/** Read the persisted decision, or null if none/expired/invalid. Falls back to
 *  the shared mirror cookie (a decision made on another fidava-pt.com host). */
export const getConsentRecord = (): ConsentRecord | null => {
    if (!isBrowser()) {
        return null;
    }
    try {
        let raw: string | null = null;
        try {
            raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
        } catch {
            /* storage unavailable — the cookie below may still have it */
        }
        const fromStorage = !!raw;
        if (!raw) {
            raw = readMirrorCookie();
        }
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as ConsentRecord;
        if (!parsed || parsed.version !== CURRENT_POLICY_VERSION || !parsed.categories) {
            return null;
        }
        // A decision made before the cookie was shared: re-write it once for the
        // parent domain, so the other fidava-pt.com hosts see it too.
        if (fromStorage && !sharedCookieSynced) {
            sharedCookieSynced = true;
            writeMirrorCookie(parsed);
        }
        return parsed;
    } catch {
        return null;
    }
};

/** True once the user has made (and we still honour) an explicit choice. */
export const hasDecision = (): boolean => getConsentRecord() !== null;

/** Current granted categories — everything denied until an explicit decision. */
export const getConsent = (): ConsentCategories => {
    const record = getConsentRecord();
    return record ? record.categories : { ...DENY_ALL };
};

/** Minimal gtag accessor — GTM defines window.dataLayer + gtag in Index.cshtml. */
const gtag = (...args: unknown[]): void => {
    if (!isBrowser()) {
        return;
    }
    const w = window as unknown as { dataLayer?: unknown[] };
    if (!Array.isArray(w.dataLayer)) {
        w.dataLayer = [];
    }
    // Push a real `arguments` object, not the rest-params array: GTM reads
    // either positionally, but gtag.js (loaded directly for GA4) only
    // recognises Arguments objects as commands — an array would leave the
    // consent update unapplied on the GA4 side.
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push((function () { return arguments; }).apply(null, args as never));
};

/** Translate our categories into a Google Consent Mode v2 update payload. */
export const applyConsentToGtag = (categories: ConsentCategories): void => {
    const granted = (value: boolean): "granted" | "denied" => (value ? "granted" : "denied");
    gtag("consent", "update", {
        analytics_storage: granted(categories.analytics),
        ad_storage: granted(categories.marketing),
        ad_user_data: granted(categories.marketing),
        ad_personalization: granted(categories.marketing),
        personalization_storage: granted(categories.preferences)
    });
};

const writeMirrorCookie = (record: ConsentRecord): void => {
    if (!isBrowser()) {
        return;
    }
    try {
        // 12-month expiry, lax — first-party, no cross-site need.
        const maxAge = 60 * 60 * 24 * 365;
        const value = encodeURIComponent(JSON.stringify(record));
        const secure = window.location.protocol === "https:" ? "; Secure" : "";
        document.cookie = `${CONSENT_COOKIE_NAME}=${value}; path=/; max-age=${maxAge}; SameSite=Lax${secure}${cookieDomainAttribute()}`;
    } catch {
        /* cookie write is best-effort */
    }
};

/**
 * Persist a decision, update Google Consent Mode, and notify subscribers
 * (trackers re-evaluate). The optional onPersist hook is where the server-side
 * proof-of-consent POST is wired (fire-and-forget — never blocks the UI).
 */
export const setConsent = (categories: ConsentCategories, locale?: string): ConsentRecord => {
    const record: ConsentRecord = {
        version: CURRENT_POLICY_VERSION,
        timestamp: new Date().toISOString(),
        categories: { ...categories },
        locale
    };
    if (isBrowser()) {
        try {
            window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
        } catch {
            /* storage may be unavailable (private mode) — cookie mirror still set */
        }
        writeMirrorCookie(record);
    }
    applyConsentToGtag(record.categories);
    listeners.forEach((listener) => {
        try {
            listener(record.categories);
        } catch {
            /* a misbehaving listener must not break the others */
        }
    });
    onPersistHandlers.forEach((handler) => {
        try {
            handler(record);
        } catch {
            /* proof-of-consent logging is best-effort */
        }
    });
    return record;
};

export const acceptAll = (locale?: string): ConsentRecord => setConsent({ ...GRANT_ALL }, locale);
export const rejectAll = (locale?: string): ConsentRecord => setConsent({ ...DENY_ALL }, locale);

/** Subscribe to consent changes. Returns an unsubscribe function. */
export const onConsentChange = (listener: ConsentListener): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

type PersistHandler = (record: ConsentRecord) => void;
const onPersistHandlers = new Set<PersistHandler>();

/**
 * Register a side-effect to run whenever a decision is persisted (e.g. the
 * server-side audit POST). Returns an unregister function.
 */
export const onConsentPersisted = (handler: PersistHandler): (() => void) => {
    onPersistHandlers.add(handler);
    return () => {
        onPersistHandlers.delete(handler);
    };
};

/** Convenience for trackers: only run their side-effect if analytics is granted. */
export const isAnalyticsGranted = (): boolean => getConsent().analytics;
export const isMarketingGranted = (): boolean => getConsent().marketing;
